/**
 * API Service - Replaces localStorage with backend API calls
 */

const API_BASE = '/api';

// Types (preserved from localStorageStore.ts)
export interface Product {
    id: string;
    name: string;
    price: number;
    description: string;
    descriptionEn?: string;
    fullDescription?: string;
    fullDescriptionEn?: string;
    category: string;
    subcategory?: string;
    region?: string;
    year?: number;
    volume?: string;
    alcohol?: string;
    image?: string;
    images: string[];
    grapeVariety?: string;
    servingTemp?: string;
    aging?: string;
    pairing?: string[];
    stock: number;
    status: 'active' | 'draft' | 'out_of_stock' | 'pos_only';
    weight?: string;
    origin?: string;
    contents?: string[];
    sweetness?: 'dry' | 'semi-dry' | 'semi-sweet' | 'sweet';
    fizziness?: 'still' | 'sparkling';
    createdAt: string;
    updatedAt: string;
    isFeatured?: boolean;
    salePrice?: number | null;
    promotionExpiresAt?: string | null;
    sortOrder?: number;
    ean?: string | null;
    inStoreOnly?: boolean;
}

export interface Category {
    id: string;
    name: string;
    displayName: string;
    displayNameEn?: string;
    color: string;
    subcategories?: string[];
    productCount: number;
    showInNav?: boolean;
    sortOrder?: number;
}

export interface Order {
    id: string;
    orderNumber: string;
    customer: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
    };
    shippingAddress: {
        street: string;
        apartment?: string;
        city: string;
        postalCode: string;
        country: string;
    };
    items: {
        productId: string;
        name: string;
        price: number;
        quantity: number;
        image: string;
    }[];
    subtotal: number;
    discountCode?: string;
    discountAmount: number;
    shippingCost: number;
    deliveryMethod: string;
    inpostPoint?: any;
    dhlPoint?: any;
    total: number;
    paymentMethod: string;
    paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
    status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded' | 'ready_for_pickup' | 'picked_up';
    trackingNumber?: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
    paymentUrl?: string;
    paymentUrlError?: string;
}

export interface DiscountCode {
    id: string;
    code: string;
    type: 'percentage' | 'fixed';
    value: number;
    minOrderValue: number;
    maxUses: number;
    currentUses: number;
    validFrom: string;
    validTo: string;
    isActive: boolean;
    applicableCategories?: string[];
    createdAt: string;
}

export interface Customer {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    ordersCount: number;
    totalSpent: number;
    createdAt: string;
    lastOrderAt?: string;
    marketingConsent?: boolean;
}

export interface Opinion {
    id: string;
    name: string;
    text: string;
    rating: number;
    date: string;
    createdAt: string;
}

export interface BlogPost {
    id: string;
    title: string;
    excerpt?: string;
    content: string;
    image?: string;
    status: 'draft' | 'published';
    createdAt: string;
    updatedAt?: string;
}

export interface OpeningHourDay {
    day: string;
    open: string;
    close: string;
    closed: boolean;
}

export interface DeliveryMethod {
    id: string;
    name: string;
    cost: number;
    enabled: boolean;
    type: 'custom' | 'inpost' | 'dhl' | 'pickup';
    description?: string;
}

export interface EventItem {
    title: string;
    date: string;
}

export interface AnnouncementItem {
    text: string;
    link?: string;
    linkText?: string;
}

export interface ActivityLog {
    id: string;
    user_name: string;
    action: string;
    entity_type: string;
    entity_id: string | null;
    details: any;
    created_at: string;
}

export interface StoreSettings {
    id?: string;
    hero_title: string;
    hero_subtitle: string;
    storeName?: string;
    currency?: string;
    email: string;
    phone: string;
    address: string;
    facebook_link: string;
    instagram_link: string;
    opening_hours: string | OpeningHourDay[];
    google_maps_link: string;
    shippingCost?: number;
    inpostShippingCost?: number;
    dhlShippingCost?: number;
    freeShippingThreshold?: number;
    inpostToken?: string;
    dhlToken?: string;
    delivery_methods: string | DeliveryMethod[];
    events?: string | EventItem[];
    maintenanceMode?: boolean;
    maintenanceText?: string;
    smtp_host?: string;
    smtp_auth?: boolean;
    smtp_username?: string;
    smtp_password?: string;
    smtp_secure?: string;
    smtp_port?: number;
    from_email?: string;
    from_name?: string;
    announcement_text?: string;
    announcement_link?: string;
    announcement_link_text?: string;
    announcements?: string | AnnouncementItem[];
    announcements_enabled?: boolean;
    announcements_marquee?: boolean;
    updated_at?: string;
}

export interface DashboardStats {
    totalRevenue: number;
    todayRevenue: number;
    ordersCount: number;
    todayOrdersCount: number;
    productsCount: number;
    customersCount: number;
    pendingOrders: number;
    lowStockProducts: number;
    averageOrderValue: number;
    salesByDay: number[];
    topProducts: { id: string; name: string; count: number; revenue: number }[];
    recentOrders: Order[];
}

export const AUTH_TOKEN_KEY = 'admin_auth_token';

// Generic fetch wrapper with error handling
async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string> || {})
    };

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
    });

    if (response.status === 401) {
        // Auto logout on unauthorized
        localStorage.removeItem(AUTH_TOKEN_KEY);
        // Dispatch an event so components can react
        window.dispatchEvent(new Event('auth:unauthorized'));
    }

    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `API Error: ${response.status}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
        return {} as T;
    }

    return response.json();
}

// ============================================
// AUTH API
// ============================================
export const authApi = {
    login: async (username: string, password: string): Promise<{ token: string }> => {
        const res = await fetchApi<{ token: string }>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });
        if (res.token) {
            localStorage.setItem(AUTH_TOKEN_KEY, res.token);
            window.dispatchEvent(new Event('auth:login'));
        }
        return res;
    },
    logout: async (): Promise<void> => {
        try {
            await fetchApi('/auth/logout', { method: 'POST' });
        } catch (e) {
            // Ignore errors on logout
        } finally {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            window.dispatchEvent(new Event('auth:logout'));
        }
    },
    validate: async (): Promise<{ valid: boolean }> => {
        if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
            return { valid: false };
        }
        try {
            return await fetchApi<{ valid: boolean }>('/auth/validate', { method: 'POST' });
        } catch (e) {
            return { valid: false };
        }
    },
    changePassword: (oldPassword: string, newPassword: string): Promise<{ success: boolean }> => {
        return fetchApi('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify({ oldPassword, newPassword }),
        });
    },
    changeUsername: (newUsername: string): Promise<{ success: boolean }> => {
        return fetchApi('/auth/change-username', {
            method: 'POST',
            body: JSON.stringify({ newUsername }),
        });
    },
    updateCredentials: (data: { newUsername?: string; oldPassword?: string; newPassword?: string }): Promise<{ success: boolean }> => {
        return fetchApi('/auth/change-credentials', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },
    getToken: () => localStorage.getItem(AUTH_TOKEN_KEY),
    isAuthenticated: () => !!localStorage.getItem(AUTH_TOKEN_KEY)
};

// ============================================
// UPLOAD API
// ============================================
export const uploadApi = {
    uploadImages: async (files: File[], folder?: string): Promise<{ paths: string[] }> => {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('images[]', file);
        });
        if (folder) {
            formData.append('folder', folder);
        }

        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE}/upload`, {
            method: 'POST',
            body: formData,
            headers,
        });

        if (response.status === 401) {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            window.dispatchEvent(new Event('auth:unauthorized'));
        }

        if (!response.ok) {
            const error = await response.json().catch(async () => {
                const text = await response.text().catch(() => '');
                return { error: `Upload failed (${response.status}). ${text.substring(0, 100)}` };
            });
            throw new Error(error.error || 'Upload failed');
        }

        return response.json();
    },

    deleteImage: async (path: string): Promise<{ success: boolean }> => {
        return fetchApi<{ success: boolean }>('/upload', {
            method: 'DELETE',
            body: JSON.stringify({ path }),
        });
    },
};

// ============================================
// GALLERY API
// ============================================
export const galleryApi = {
    getGallery: (): Promise<string[]> => {
        return fetchApi<string[]>('/gallery');
    }
};

// ============================================
// PRODUCTS API
// ============================================
export const productsApi = {
    getAll: (filters?: {
        category?: string;
        subcategory?: string;
        search?: string;
        minPrice?: number;
        maxPrice?: number;
        status?: string;
    }): Promise<Product[]> => {
        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value));
                }
            });
        }
        const query = params.toString();
        return fetchApi<Product[]>(`/products${query ? `?${query}` : ''}`);
    },

    getById: (id: string): Promise<Product> => {
        return fetchApi<Product>(`/products/${id}`);
    },

    create: (data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<Product> => {
        return fetchApi<Product>('/products', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    update: (id: string, data: Partial<Product>): Promise<Product> => {
        return fetchApi<Product>(`/products/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete: (id: string): Promise<{ success: boolean }> => {
        return fetchApi<{ success: boolean }>(`/products/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============================================
// CATEGORIES API
// ============================================
export const categoriesApi = {
    getAll: (): Promise<Category[]> => {
        return fetchApi<Category[]>('/categories');
    },

    getById: (id: string): Promise<Category> => {
        return fetchApi<Category>(`/categories/${id}`);
    },

    create: (data: Omit<Category, 'id' | 'productCount'>): Promise<Category> => {
        return fetchApi<Category>('/categories', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    update: (id: string, data: Partial<Category>): Promise<Category> => {
        return fetchApi<Category>(`/categories/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete: (id: string): Promise<{ success: boolean }> => {
        return fetchApi<{ success: boolean }>(`/categories/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============================================
// ORDERS API
// ============================================
export const ordersApi = {
    getAll: (filters?: {
        status?: string;
        dateFrom?: string;
        dateTo?: string;
    }): Promise<Order[]> => {
        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, String(value));
                }
            });
        }
        const query = params.toString();
        return fetchApi<Order[]>(`/orders${query ? `?${query}` : ''}`);
    },

    getById: (id: string): Promise<Order> => {
        return fetchApi<Order>(`/orders/${id}`);
    },

    create: (data: Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'>): Promise<Order> => {
        return fetchApi<Order>('/orders', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    updateStatus: (id: string, status: Order['status'], trackingNumber?: string): Promise<Order> => {
        return fetchApi<Order>(`/orders/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status, trackingNumber }),
        });
    },

    update: (id: string, data: any): Promise<Order> => {
        return fetchApi<Order>(`/orders/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete: (id: string): Promise<{ success: boolean }> => {
        return fetchApi<{ success: boolean }>(`/orders/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============================================
// DISCOUNT CODES API
// ============================================
export const discountCodesApi = {
    getAll: (): Promise<DiscountCode[]> => {
        return fetchApi<DiscountCode[]>('/discounts');
    },

    getById: (id: string): Promise<DiscountCode> => {
        return fetchApi<DiscountCode>(`/discounts/${id}`);
    },

    validate: (code: string, cartTotal: number): Promise<{
        valid: boolean;
        discountAmount: number;
        message: string;
        discountCode?: DiscountCode;
    }> => {
        return fetchApi('/discounts/validate', {
            method: 'POST',
            body: JSON.stringify({ code, cartTotal }),
        });
    },

    useCode: (code: string): Promise<{ success: boolean }> => {
        return fetchApi('/discounts/use', {
            method: 'POST',
            body: JSON.stringify({ code }),
        });
    },

    create: (data: Omit<DiscountCode, 'id' | 'currentUses' | 'createdAt'>): Promise<DiscountCode> => {
        return fetchApi<DiscountCode>('/discounts', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    update: (id: string, data: Partial<DiscountCode>): Promise<DiscountCode> => {
        return fetchApi<DiscountCode>(`/discounts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete: (id: string): Promise<{ success: boolean }> => {
        return fetchApi<{ success: boolean }>(`/discounts/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============================================
// CUSTOMERS API
// ============================================
export const customersApi = {
    getAll: (): Promise<Customer[]> => {
        return fetchApi<Customer[]>('/customers');
    },
    getMarketingConsented: (): Promise<Customer[]> => {
        return fetchApi<Customer[]>('/customers?marketing=true');
    },
    delete: (id: string): Promise<{ success: boolean }> => {
        return fetchApi<{ success: boolean }>(`/customers/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============================================
// OPINIONS API
// ============================================
export const opinionsApi = {
    getAll: (): Promise<Opinion[]> => {
        return fetchApi<Opinion[]>('/opinions');
    },

    getById: (id: string): Promise<Opinion> => {
        return fetchApi<Opinion>(`/opinions/${id}`);
    },

    create: (data: Omit<Opinion, 'id' | 'createdAt'>): Promise<Opinion> => {
        return fetchApi<Opinion>('/opinions', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    update: (id: string, data: Partial<Opinion>): Promise<Opinion> => {
        return fetchApi<Opinion>(`/opinions/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete: (id: string): Promise<{ success: boolean }> => {
        return fetchApi<{ success: boolean }>(`/opinions/${id}`, {
            method: 'DELETE',
        });
    },
};

// ============================================
// BLOG API
// ============================================
export const blogApi = {
    getAll: (limit?: number): Promise<BlogPost[]> => {
        return fetchApi<BlogPost[]>(`/blog${limit ? `?limit=${limit}` : ''}`);
    },
    getBySlug: (slug: string): Promise<BlogPost> => {
        return fetchApi<BlogPost>(`/blog/${slug}`);
    },
    create: (data: Omit<BlogPost, 'createdAt' | 'updatedAt'>): Promise<BlogPost> => {
        return fetchApi<BlogPost>('/blog', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },
    update: (slug: string, data: Partial<BlogPost>): Promise<BlogPost> => {
        return fetchApi<BlogPost>(`/blog/${slug}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },
    delete: (slug: string): Promise<{ success: boolean }> => {
        return fetchApi<{ success: boolean }>(`/blog/${slug}`, {
            method: 'DELETE',
        });
    }
};

// ============================================
// DASHBOARD API
// ============================================
export const dashboardApi = {
    getStats: (): Promise<DashboardStats> => {
        return fetchApi<DashboardStats>('/dashboard/stats');
    },
};

// ============================================
// SETTINGS API
// ============================================
export const settingsApi = {
    get: (): Promise<StoreSettings> => {
        return fetchApi<StoreSettings>('/settings');
    },
    update: (data: Partial<StoreSettings>): Promise<StoreSettings> => {
        return fetchApi<StoreSettings>('/settings', {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },
};

// ============================================
// NOTIFICATIONS API
// ============================================
export const notificationsApi = {
    getReadIds: (): Promise<string[]> => {
        return fetchApi<string[]>('/notifications/read');
    },
    markAsRead: (ids: string[]): Promise<{ success: boolean }> => {
        return fetchApi('/notifications/read', {
            method: 'POST',
            body: JSON.stringify({ ids }),
        });
    },
};

// ============================================
// MARKETING API
// ============================================
export interface Campaign {
    id: string;
    subject: string;
    recipientsCount: number;
    sent: number;
    failed: number;
    createdAt: string;
}

export const marketingApi = {
    sendCampaign: (data: {
        recipients: string[];
        subject: string;
        templateId: string;
        heading: string;
        message: string;
        ctaText?: string;
        ctaUrl?: string;
        customHtml?: string;
    }): Promise<{ sent: number; failed: number; errors: string[]; campaign: Campaign }> => {
        return fetchApi('/marketing/send', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    getCampaigns: (): Promise<Campaign[]> => {
        return fetchApi<Campaign[]>('/marketing/campaigns');
    },
};

// ============================================
// IMAGE HELPER (preserved from localStorageStore)
// ============================================
import { productImages } from '@/lib/images';

export const getProductImageUrl = (imageName: string): string => {
    return productImages[imageName as keyof typeof productImages] || '';
};

// ============================================
// LEGACY COMPATIBILITY LAYER
// For gradual migration - wraps async API in sync-like interface
// ============================================

// Cache for sync access
const safeParse = <T>(key: string, defaultVal: T): T => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultVal;
    } catch {
        return defaultVal;
    }
};

let productsCache: Product[] = safeParse<Product[]>('wina_productsCache', []);
let categoriesCache: Category[] = safeParse<Category[]>('wina_categoriesCache', []);
let ordersCache: Order[] = [];
let discountCodesCache: DiscountCode[] = [];
let customersCache: Customer[] = [];
let opinionsCache: Opinion[] = [];
let readNotificationsCache: string[] = [];
let settingsCache: StoreSettings | null = safeParse<StoreSettings | null>('wina_settingsCache', null);
const settingsListeners: ((settings: StoreSettings) => void)[] = [];
const categoriesListeners: ((categories: Category[]) => void)[] = [];

// Initialize cache
export async function initializeCache(): Promise<void> {
    try {
        const isAuthenticated = authApi.isAuthenticated();

        const [products, categories, settings, opinions] = await Promise.all([
            productsApi.getAll().catch(() => []),
            categoriesApi.getAll().catch(() => []),
            settingsApi.get().catch(() => null),
            opinionsApi.getAll().catch(() => []),
        ]);

        let orders: any[] = [];
        let discounts: any[] = [];
        let customers: any[] = [];
        let readNotifIds: string[] = [];

        if (isAuthenticated) {
            [orders, discounts, customers, readNotifIds] = await Promise.all([
                ordersApi.getAll().catch(() => []),
                discountCodesApi.getAll().catch(() => []),
                customersApi.getAll().catch(() => []),
                notificationsApi.getReadIds().catch(() => []),
            ]);
        }

        productsCache = products;
        categoriesCache = categories;
        ordersCache = orders;
        discountCodesCache = discounts;
        customersCache = customers;
        opinionsCache = opinions;
        readNotificationsCache = readNotifIds || [];
        settingsCache = settings;

        // Persist to local storage to prevent layout shift on next reload
        localStorage.setItem('wina_productsCache', JSON.stringify(productsCache));
        localStorage.setItem('wina_categoriesCache', JSON.stringify(categoriesCache));
        if (settings) {
            localStorage.setItem('wina_settingsCache', JSON.stringify(settings));
            if (typeof settings.delivery_methods === 'string') {
                try {
                    settings.delivery_methods = JSON.parse(settings.delivery_methods);
                } catch (e) {
                    settings.delivery_methods = [];
                }
            }
            settingsStore.notify(settings);
        }
        // Notify categories listeners
        categoriesListeners.forEach(fn => fn(categoriesCache));
    } catch (error) {
        console.error('Failed to initialize cache:', error);
    }
}

// Sync-like stores for backward compatibility (uses cache)
export const productsStore = {
    getAll: () => productsCache,
    getById: (id: string) => productsCache.find(p => p.id === id),
    getFiltered: (filters: { category?: string; subcategory?: string; search?: string; minPrice?: number; maxPrice?: number; status?: string }) => {
        let result = [...productsCache];
        if (filters.category) result = result.filter(p => p.category === filters.category);
        if (filters.subcategory) result = result.filter(p => p.subcategory === filters.subcategory);
        if (filters.status) result = result.filter(p => p.status === filters.status);
        if (filters.minPrice !== undefined) result = result.filter(p => p.price >= filters.minPrice!);
        if (filters.maxPrice !== undefined) result = result.filter(p => p.price <= filters.maxPrice!);
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            result = result.filter(p => p.name.toLowerCase().includes(searchLower) || p.description?.toLowerCase().includes(searchLower));
        }
        return result;
    },
    create: async (data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => {
        const product = await productsApi.create(data);
        productsCache.push(product);
        return product;
    },
    update: async (id: string, data: Partial<Product>) => {
        const product = await productsApi.update(id, data);
        const index = productsCache.findIndex(p => p.id === id);
        if (index !== -1) productsCache[index] = product;
        return product;
    },
    delete: async (id: string) => {
        await productsApi.delete(id);
        productsCache = productsCache.filter(p => p.id !== id);
        return true;
    },
    getImageUrl: getProductImageUrl,
};

export const categoriesStore = {
    getAll: () => categoriesCache,
    getById: (id: string) => categoriesCache.find(c => c.id === id),
    subscribe: (listener: (categories: Category[]) => void) => {
        categoriesListeners.push(listener);
        return () => {
            const idx = categoriesListeners.indexOf(listener);
            if (idx !== -1) categoriesListeners.splice(idx, 1);
        };
    },
    create: async (data: Omit<Category, 'id' | 'productCount'>) => {
        const category = await categoriesApi.create(data);
        categoriesCache.push(category);
        categoriesListeners.forEach(fn => fn(categoriesCache));
        return category;
    },
    update: async (id: string, data: Partial<Category>) => {
        const category = await categoriesApi.update(id, data);
        const index = categoriesCache.findIndex(c => c.id === id);
        if (index !== -1) categoriesCache[index] = category;
        categoriesListeners.forEach(fn => fn(categoriesCache));
        return category;
    },
    delete: async (id: string) => {
        await categoriesApi.delete(id);
        categoriesCache = categoriesCache.filter(c => c.id !== id);
        categoriesListeners.forEach(fn => fn(categoriesCache));
        return true;
    },
};

export const opinionsStore = {
    getAll: () => opinionsCache,
    create: async (data: Omit<Opinion, 'id' | 'createdAt'>) => {
        const opinion = await opinionsApi.create(data);
        opinionsCache.unshift(opinion);
        return opinion;
    },
    delete: async (id: string) => {
        await opinionsApi.delete(id);
        opinionsCache = opinionsCache.filter(o => o.id !== id);
        return true;
    },
};

export const ordersStore = {
    getAll: () => ordersCache,
    getById: (id: string) => ordersCache.find(o => o.id === id),
    getByStatus: (status: Order['status']) => ordersCache.filter(o => o.status === status),
    create: async (data: Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'>) => {
        const order = await ordersApi.create(data);
        ordersCache.unshift(order);
        return order;
    },
    updateStatus: async (id: string, status: Order['status'], trackingNumber?: string) => {
        const order = await ordersApi.updateStatus(id, status, trackingNumber);
        const index = ordersCache.findIndex(o => o.id === id);
        if (index !== -1) ordersCache[index] = order;
        return order;
    },
    update: async (id: string, data: any) => {
        const order = await ordersApi.update(id, data);
        const index = ordersCache.findIndex(o => o.id === id);
        if (index !== -1) ordersCache[index] = order;
        return order;
    },
    delete: async (id: string) => {
        await ordersApi.delete(id);
        ordersCache = ordersCache.filter(o => o.id !== id);
        return true;
    },
    getStats: () => {
        const totalRevenue = ordersCache.reduce((sum, o) => sum + o.total, 0);
        const pendingOrders = ordersCache.filter(o => o.status === 'pending').length;
        return {
            totalOrders: ordersCache.length,
            totalRevenue,
            pendingOrders,
            averageOrderValue: ordersCache.length > 0 ? totalRevenue / ordersCache.length : 0,
        };
    },
};

export const discountCodesStore = {
    getAll: () => discountCodesCache,
    getById: (id: string) => discountCodesCache.find(c => c.id === id),
    getByCode: (code: string) => discountCodesCache.find(c => c.code.toUpperCase() === code.toUpperCase()),
    validate: async (code: string, cartTotal: number) => {
        return discountCodesApi.validate(code, cartTotal);
    },
    useCode: async (code: string) => {
        const result = await discountCodesApi.useCode(code);
        // Update cache
        const discount = discountCodesCache.find(c => c.code.toUpperCase() === code.toUpperCase());
        if (discount) discount.currentUses++;
        return result.success;
    },
    create: async (data: Omit<DiscountCode, 'id' | 'currentUses' | 'createdAt'>) => {
        const discount = await discountCodesApi.create(data);
        discountCodesCache.push(discount);
        return discount;
    },
    update: async (id: string, data: Partial<DiscountCode>) => {
        const discount = await discountCodesApi.update(id, data);
        const index = discountCodesCache.findIndex(c => c.id === id);
        if (index !== -1) discountCodesCache[index] = discount;
        return discount;
    },
    delete: async (id: string) => {
        await discountCodesApi.delete(id);
        discountCodesCache = discountCodesCache.filter(c => c.id !== id);
        return true;
    },
};

export const customersStore = {
    getAll: () => customersCache,
    getById: (id: string) => customersCache.find(c => c.id === id),
    create: async (data: Omit<Customer, 'id' | 'createdAt' | 'lastOrderAt'>) => {
        // Customer creation is handled by order creation on backend
        const customer = { ...data, id: '', createdAt: '', lastOrderAt: '' } as Customer;
        customersCache.push(customer);
        return customer;
    },
    getStats: () => {
        const totalSpent = customersCache.reduce((sum, c) => sum + c.totalSpent, 0);
        return {
            totalCustomers: customersCache.length,
            totalSpent,
            averageSpent: customersCache.length > 0 ? totalSpent / customersCache.length : 0,
        };
    },
    delete: async (id: string) => {
        await customersApi.delete(id);
        customersCache = customersCache.filter(c => c.id !== id);
        return true;
    },
};

export const settingsStore = {
    get: () => settingsCache || {
        hero_title: 'Włoska piwnica',
        hero_subtitle: 'Naturalne wina z beczki',
        email: 'kontakt@wloskapiwnica.pl',
        phone: '+48 123 456 789',
        address: 'ul. Winiarska 15, 00-001 Warszawa',
        facebook_link: '#',
        instagram_link: '#',
        opening_hours: [
            { day: 'mon', open: '10:00', close: '19:00', closed: false },
            { day: 'tue', open: '10:00', close: '19:00', closed: false },
            { day: 'wed', open: '10:00', close: '19:00', closed: false },
            { day: 'thu', open: '10:00', close: '19:00', closed: false },
            { day: 'fri', open: '10:00', close: '19:00', closed: false },
            { day: 'sat', open: '10:00', close: '16:00', closed: false },
            { day: 'sun', open: '10:00', close: '16:00', closed: true },
        ],
        google_maps_link: '',
        shippingCost: 15,
        inpostShippingCost: 12,
        freeShippingThreshold: 200,
        inpostToken: '',
        dhlToken: '',
        delivery_methods: [
            { id: 'inpost', name: 'InPost Paczkomaty', cost: 12, enabled: true, type: 'inpost' },
            { id: 'pickup', name: 'Odbiór w sklepie', cost: 0, enabled: true, type: 'pickup', description: 'Odbierz zamówienie osobiście w naszym sklepie' },
        ],
    },
    update: async (data: Partial<StoreSettings>) => {
        const updated = await settingsApi.update(data);
        if (typeof updated.delivery_methods === 'string') {
            try {
                updated.delivery_methods = JSON.parse(updated.delivery_methods);
            } catch (e) {
                updated.delivery_methods = [];
            }
        }
        settingsCache = updated;
        settingsStore.notify(updated);
        return updated;
    },
    subscribe: (listener: (settings: StoreSettings) => void) => {
        settingsListeners.push(listener);
        return () => {
            const index = settingsListeners.indexOf(listener);
            if (index > -1) settingsListeners.splice(index, 1);
        };
    },
    notify: (settings: StoreSettings) => {
        settingsListeners.forEach(listener => listener(settings));
    },
};

// ============================================
// MONITORING API
// ============================================
export interface MonitoringSummary {
    totalRevenue: number;
    totalOrders: number;
    averageOrderValue: number;
    totalItemsSold: number;
    onlineRevenue: number;
    posRevenue: number;
    onlineOrders: number;
    posOrders: number;
    prevTotalRevenue: number;
    prevTotalOrders: number;
    prevAverageOrderValue: number;
    prevTotalItemsSold: number;
}

export interface ProductSalesItem {
    productId: string;
    name: string;
    category: string;
    ean: string | null;
    onlineSold: number;
    posSold: number;
    totalSold: number;
    revenue: number;
    currentStock: number;
}

export interface DailyBreakdownItem {
    date: string;
    ordersCount: number;
    revenue: number;
    itemsSold: number;
    onlineOrders: number;
    posOrders: number;
}

export interface HourlySalesItem {
    hour: number;
    ordersCount: number;
    revenue: number;
    itemsSold: number;
}

export interface StockAuditItem {
    productId: string;
    name: string;
    category: string;
    ean: string | null;
    currentStock: number;
    totalEverSold: number;
    calculatedInitialStock: number;
    discrepancy: number;
}

export interface ChannelBreakdownItem {
    channel: string;
    label: string;
    ordersCount: number;
    revenue: number;
    itemsSold: number;
}

export interface TransactionItem {
    id: string;
    orderNumber: string;
    createdAt: string;
    customerName: string;
    items: { name: string; quantity: number }[];
    totalAmount: number;
    paymentMethod: string;
    paymentMethodRaw: string;
    status: string;
    channel: string;
}

export interface TransactionsResponse {
    data: TransactionItem[];
    totalCount: number;
    page: number;
    perPage: number;
    totalPages: number;
}

function buildMonitoringParams(params: Record<string, string | number | undefined>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            searchParams.set(key, String(value));
        }
    });
    const str = searchParams.toString();
    return str ? `?${str}` : '';
}

export const monitoringApi = {
    getSummary: (dateFrom?: string, dateTo?: string) =>
        fetchApi<MonitoringSummary>(`/monitoring/summary${buildMonitoringParams({ dateFrom, dateTo })}`),
    
    getSalesByProduct: (dateFrom?: string, dateTo?: string, category?: string) =>
        fetchApi<ProductSalesItem[]>(`/monitoring/sales-by-product${buildMonitoringParams({ dateFrom, dateTo, category })}`),
    
    getDailyBreakdown: (dateFrom?: string, dateTo?: string) =>
        fetchApi<DailyBreakdownItem[]>(`/monitoring/daily-breakdown${buildMonitoringParams({ dateFrom, dateTo })}`),
    
    getHourlySales: (dateFrom?: string, dateTo?: string) =>
        fetchApi<HourlySalesItem[]>(`/monitoring/hourly-sales${buildMonitoringParams({ dateFrom, dateTo })}`),
    
    getStockAudit: () =>
        fetchApi<StockAuditItem[]>('/monitoring/stock-audit'),
    
    getChannelBreakdown: (dateFrom?: string, dateTo?: string) =>
        fetchApi<ChannelBreakdownItem[]>(`/monitoring/channel-breakdown${buildMonitoringParams({ dateFrom, dateTo })}`),
    
    getTransactions: (params: { dateFrom?: string; dateTo?: string; search?: string; channel?: string; page?: number; perPage?: number; productId?: string }) =>
        fetchApi<TransactionsResponse>(`/monitoring/transactions${buildMonitoringParams(params as any)}`),
};

// ============================================
// NOTIFICATIONS STORE
// ============================================
export const notificationsStore = {
    getReadIds: () => readNotificationsCache,
    markAsRead: async (ids: string[]) => {
        const newIds = ids.filter(id => !readNotificationsCache.includes(id));
        if (newIds.length > 0) {
            readNotificationsCache = [...readNotificationsCache, ...newIds];
            try {
                await notificationsApi.markAsRead(newIds);
            } catch (error) {
                console.error('Błąd oznaczania powiadomień:', error);
            }
        }
    }
};

export const getDashboardStats = (): DashboardStats => {
    // Calculate stats from cached data (sync)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const todayOrders = ordersCache.filter(o => new Date(o.createdAt) >= today);
    const todayRevenue = todayOrders.reduce((sum, o) => sum + o.total, 0);

    // Sales by day (last 7 days)
    const salesByDay: number[] = [];
    for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);

        const dayOrders = ordersCache.filter(o => {
            const orderDate = new Date(o.createdAt);
            return orderDate >= date && orderDate < nextDate;
        });
        salesByDay.push(dayOrders.reduce((sum, o) => sum + o.total, 0));
    }

    // Top products
    const productSales: { [key: string]: { name: string; count: number; revenue: number } } = {};
    ordersCache.forEach(order => {
        order.items.forEach(item => {
            if (!productSales[item.productId]) {
                productSales[item.productId] = { name: item.name, count: 0, revenue: 0 };
            }
            productSales[item.productId].count += item.quantity;
            productSales[item.productId].revenue += item.price * item.quantity;
        });
    });
    const topProducts = Object.entries(productSales)
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

    return {
        totalRevenue: ordersCache.reduce((sum, o) => sum + o.total, 0),
        todayRevenue,
        ordersCount: ordersCache.length,
        todayOrdersCount: todayOrders.length,
        productsCount: productsCache.length,
        customersCount: customersCache.length,
        pendingOrders: ordersCache.filter(o => o.status === 'pending').length,
        lowStockProducts: productsCache.filter(p => p.stock < 10 && p.status !== 'draft' && p.status !== 'pos_only' && p.category !== 'pos').length,
        averageOrderValue: ordersCache.length > 0 ? ordersCache.reduce((sum, o) => sum + o.total, 0) / ordersCache.length : 0,
        salesByDay,
        topProducts,
        recentOrders: ordersCache.slice(0, 5),
    };
};


export const resetStore = () => {
    console.warn('resetStore is not available with database backend');
};

// Refresh cache helper
export async function refreshCache(): Promise<void> {
    await initializeCache();
}

// ============================================================================
// DELIVERIES API
// ============================================================================
export interface Delivery {
    id: string;
    product_id: string;
    quantity: number;
    created_at: string;
    note: string | null;
    user_id: string | null;
    product_name?: string;
    product_category?: string;
}

export interface DeliveriesResponse {
    data: Delivery[];
    totalCount: number;
    page: number;
    totalPages: number;
}

export const deliveriesApi = {
    getDeliveries: (page = 1, perPage = 50) => 
        fetchApi<DeliveriesResponse>(`/deliveries?page=${page}&perPage=${perPage}`),
    
    addDelivery: (data: { productId: string; quantity: number; note?: string }) => 
        fetchApi<{ success: boolean; id: string }>('/deliveries', {
            method: 'POST',
            body: JSON.stringify(data)
        }),
        
    deleteDelivery: (id: string) => 
        fetchApi<{ success: boolean }>(`/deliveries?id=${id}`, {
            method: 'DELETE'
        }),

    updateDelivery: (id: string, data: { productId: string; quantity: number; note?: string }) =>
        fetchApi<{ success: boolean }>(`/deliveries?id=${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        }),
};

// ============================================================================
// LOGS API
// ============================================================================
export interface LogsResponse {
    data: ActivityLog[];
    total: number;
    limit: number;
    offset: number;
}

export const logsApi = {
    getLogs: (limit = 100, offset = 0) => 
        fetchApi<LogsResponse>(`/logs?limit=${limit}&offset=${offset}`),
    
    addLog: (data: { action: string; entity_type: string; entity_id?: string; details?: any; user_name?: string }) => 
        fetchApi<{ success: boolean; id: string }>('/logs', {
            method: 'POST',
            body: JSON.stringify(data)
        }),
};
